import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { 
  User, 
  Shield, 
  Bell, 
  Globe, 
  Moon,
  LogOut,
  ChevronRight,
  Mail,
  Key,
  Smartphone,
  CreditCard,
  HelpCircle,
  FileText,
  AlertCircle,
  Check,
  Copy,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Zap,
  X,
  Package,
  ShoppingBag,
  Lock,
  ArrowRight
} from 'lucide-react';
import { logoutUser } from '../store/userSlice';
import { fetchOrderHistoryByUid, fetchOrderByOrderId } from '../api/orderApi';
import { activateCard } from '../api/user';
// import HomepageSettings from './HomepageSettings'; // 项目级别配置，不开放给用户

export default function SettingsPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [savedMessage, setSavedMessage] = useState('');
  const [cardKeys, setCardKeys] = useState([]);
  const [loadingCards, setLoadingCards] = useState(false);
  const [cardKeysExpanded, setCardKeysExpanded] = useState(false);
  const [copySuccess, setCopySuccess] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [activatingCard, setActivatingCard] = useState(null);
  const [showActivationTutorial, setShowActivationTutorial] = useState(false);

  // 获取用户所有卡密
  const fetchUserCardKeys = async () => {
    if (!user?.uid) return;
    
    setLoadingCards(true);
    try {
      // 1. 获取用户订单历史
      const orderHistoryResponse = await fetchOrderHistoryByUid({ uid: user.uid });
      
      if (orderHistoryResponse?.data?.orders) {
        const orders = orderHistoryResponse.data.orders;
        const allCardKeys = [];
        
        // 2. 对每个订单获取详情，提取卡密信息
        for (const order of orders) {
          try {
            const orderDetailResponse = await fetchOrderByOrderId({ orderId: order.out_trade_no });
            if (orderDetailResponse?.query_key_result && Array.isArray(orderDetailResponse.query_key_result)) {
              // 为每个卡密添加订单信息
              const keysWithOrderInfo = orderDetailResponse.query_key_result.map(key => ({
                ...key,
                orderNumber: order.out_trade_no,
                orderDate: order.created_at,
                productName: order.product_name || order.name
              }));
              allCardKeys.push(...keysWithOrderInfo);
            }
          } catch (error) {
            console.error(`获取订单 ${order.out_trade_no} 详情失败:`, error);
          }
        }
        
        setCardKeys(allCardKeys);
      }
    } catch (error) {
      console.error('获取用户卡密失败:', error);
    } finally {
      setLoadingCards(false);
    }
  };

  // 页面加载时获取卡密
  useEffect(() => {
    if (user?.uid) {
      fetchUserCardKeys();
    }
  }, [user?.uid]);

  // 复制文本到剪贴板
  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(type);
      setShowToast(true);
      setTimeout(() => {
        setCopySuccess('');
        setShowToast(false);
      }, 2000);
    } catch (err) {
      console.error('复制失败:', err);
    }
  };

  // 批量复制所有卡密
  const copyAllCardKeys = async () => {
    if (cardKeys.length === 0) return;
    
    const allKeys = cardKeys.map(key => key.card_key_display).join('\n');
    await copyToClipboard(allKeys, 'allCards');
  };

  // 卡密脱敏显示函数
  const maskCardKey = (cardKey) => {
    if (!cardKey || cardKey.length <= 8) return cardKey;
    const start = cardKey.substring(0, 4);
    const end = cardKey.substring(cardKey.length - 4);
    const middle = '*'.repeat(Math.min(cardKey.length - 8, 8));
    return `${start}${middle}${end}`;
  };

  // 激活卡密
  const handleActivateCard = async (cardKey) => {
    if (!user?.uid) return;
    
    setActivatingCard(cardKey.card_key_display);
    try {
      const response = await activateCard({ 
        uid: user.uid, 
        card_code: cardKey.card_key_display 
      });
      
      if (response?.success) {
        // 激活成功，刷新卡密列表
        await fetchUserCardKeys();
        setSavedMessage('卡密激活成功！');
        setTimeout(() => setSavedMessage(''), 3000);
      } else {
        throw new Error(response?.message || '激活失败');
      }
    } catch (error) {
      console.error('激活卡密失败:', error);
      setSavedMessage('激活失败：' + error.message);
      setTimeout(() => setSavedMessage(''), 3000);
    } finally {
      setActivatingCard(null);
    }
  };
  
  // 设置项数据
  const settingsSections = [];

  // 处理退出登录
  const handleLogout = () => {
    dispatch(logoutUser());
    navigate('/');
  };

  // 处理开关切换
  const handleToggle = (sectionIndex, itemIndex) => {
    console.log('切换开关:', sectionIndex, itemIndex);
    // 这里可以更新状态
  };

  // 显示保存成功消息
  const showSavedNotification = () => {
    setSavedMessage('设置已保存');
    setTimeout(() => setSavedMessage(''), 3000);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 页面头部 */}
      <section className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">设置</h1>
              <p className="text-sm text-gray-500 mt-1">管理您的账户和偏好设置</p>
            </div>
            {savedMessage && (
              <div className="flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-lg animate-fade-in">
                <Check className="w-4 h-4" />
                <span className="text-sm font-medium">{savedMessage}</span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* 主要内容区域 */}
      <section className="py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* 用户信息卡片 */}
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-8 mb-8 text-white shadow-xl">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                <User className="w-10 h-10 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">{user?.name || user?.email || '用户'}</h2>
                <p className="text-orange-100 mt-1">ID: {user?.uid || user?.id || 'N/A'}</p>
              </div>
            </div>
          </div>


          {/* 快捷功能入口 */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">快捷功能</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* 我的订单 */}
              <button
                onClick={() => navigate('/orders-sidebar')}
                className="bg-white rounded-xl p-4 border border-gray-200 hover:shadow-md transition-all duration-200 group"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                    <Package className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 text-left">
                    <h4 className="font-semibold text-gray-900 mb-1">我的订单</h4>
                    <p className="text-sm text-gray-500">查看历史订单和交易记录</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
                </div>
              </button>

              {/* AI商城 */}
              <button
                onClick={() => navigate('/shop')}
                className="bg-white rounded-xl p-4 border border-gray-200 hover:shadow-md transition-all duration-200 group"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center flex-shrink-0">
                    <ShoppingBag className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 text-left">
                    <h4 className="font-semibold text-gray-900 mb-1">AI商城</h4>
                    <p className="text-sm text-gray-500">浏览和购买AI服务产品</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
                </div>
              </button>

              {/* 修改密码 */}
              <button
                onClick={() => navigate('/change-password')}
                className="bg-white rounded-xl p-4 border border-gray-200 hover:shadow-md transition-all duration-200 group"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center flex-shrink-0">
                    <Lock className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 text-left">
                    <h4 className="font-semibold text-gray-900 mb-1">修改密码</h4>
                    <p className="text-sm text-gray-500">更新您的账户密码</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
                </div>
              </button>

              {/* 卡密激活 */}
              <button
                onClick={() => navigate('/card-activation')}
                className="bg-white rounded-xl p-4 border border-gray-200 hover:shadow-md transition-all duration-200 group"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                    <Key className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 text-left">
                    <h4 className="font-semibold text-gray-900 mb-1">卡密激活</h4>
                    <p className="text-sm text-gray-500">激活新购买的卡密</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
                </div>
              </button>
            </div>
          </div>

          {/* 首页设置功能已移除 - 这是项目级别配置，不开放给用户 */}

          {/* 设置项列表 */}
          <div className="space-y-6">
            {settingsSections.map((section, sectionIndex) => {
              const SectionIcon = section.icon;
              
              return (
                <div key={section.title} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                  {/* 分组标题 */}
                  <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${section.bgGradient} flex items-center justify-center`}>
                        <SectionIcon className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">{section.title}</h3>
                    </div>
                  </div>
                  
                  {/* 设置项 */}
                  <div className="divide-y divide-gray-100">
                    {section.items.map((item, itemIndex) => {
                      const ItemIcon = item.icon;
                      
                      return (
                        <div
                          key={item.label}
                          className="px-6 py-4 hover:bg-gray-50 transition-colors cursor-pointer"
                          onClick={() => !item.toggle && item.action()}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                                <ItemIcon className="w-5 h-5 text-gray-600" />
                              </div>
                              <div>
                                <h4 className="text-base font-medium text-gray-900">{item.label}</h4>
                                <p className="text-sm text-gray-500 mt-0.5">{item.description}</p>
                              </div>
                            </div>
                            
                            {/* 右侧内容 */}
                            <div className="flex items-center gap-3">
                              {item.value && (
                                <span className="text-sm text-gray-600">{item.value}</span>
                              )}
                              {item.toggle ? (
                                <label className="relative inline-flex items-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    className="sr-only peer"
                                    checked={item.checked}
                                    onChange={() => handleToggle(sectionIndex, itemIndex)}
                                  />
                                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
                                </label>
                              ) : (
                                <ChevronRight className="w-5 h-5 text-gray-400" />
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* 退出登录按钮 */}
          <div className="mt-8">
            <button
              onClick={() => setShowLogoutModal(true)}
              className="w-full bg-red-50 text-red-600 py-4 rounded-2xl font-semibold hover:bg-red-100 transition-colors flex items-center justify-center gap-2"
            >
              <LogOut className="w-5 h-5" />
              退出登录
            </button>
          </div>

          {/* 版本信息 */}
          <div className="mt-8 text-center text-sm text-gray-500">
            <p>AI Monkey v1.0.0</p>
            <p className="mt-1">© 2024 AI Monkey. All rights reserved.</p>
          </div>
        </div>
      </section>

      {/* 退出确认弹窗 */}
      {showLogoutModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 animate-fade-in-scale">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <LogOut className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 text-center mb-2">确定要退出吗？</h3>
            <p className="text-gray-600 text-center mb-6">退出后需要重新登录才能使用所有功能</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowLogoutModal(false)}
                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleLogout}
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition-colors"
              >
                确定退出
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 卡密激活教程弹窗 */}
      {showActivationTutorial && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto animate-fade-in-scale">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <HelpCircle className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">卡密激活教程</h3>
                    <p className="text-sm text-gray-500">详细的卡密激活步骤说明</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowActivationTutorial(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              {/* 教程步骤 */}
              <div className="space-y-6">
                {/* 步骤1 */}
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
                    1
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-2">找到您的卡密</h4>
                    <p className="text-gray-600 text-sm mb-3">
                      在上方的"我的卡密"列表中，找到您需要激活的卡密。未激活的卡密会显示"未激活"标识。
                    </p>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-blue-700 text-sm">
                        💡 提示：卡密通常在您购买产品后立即生成，可能需要等待几分钟才会显示。
                      </p>
                    </div>
                  </div>
                </div>

                {/* 步骤2 */}
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
                    2
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-2">点击激活按钮</h4>
                    <p className="text-gray-600 text-sm mb-3">
                      在卡密条目的右侧，点击黄色的"激活"按钮。系统会自动处理激活请求。
                    </p>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-center gap-2">
                      <Zap className="w-4 h-4 text-yellow-500" />
                      <span className="text-sm text-gray-700">激活按钮示例</span>
                    </div>
                  </div>
                </div>

                {/* 步骤3 */}
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
                    3
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-2">等待激活完成</h4>
                    <p className="text-gray-600 text-sm mb-3">
                      激活过程通常需要几秒钟时间。激活成功后，卡密状态会自动更新为"已激活"。
                    </p>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <p className="text-green-700 text-sm">
                        ✅ 激活成功后，您就可以开始使用相应的服务了！
                      </p>
                    </div>
                  </div>
                </div>

                {/* 步骤4 */}
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
                    4
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-2">开始使用服务</h4>
                    <p className="text-gray-600 text-sm mb-3">
                      激活成功后，您可以通过侧边栏的"AI车队"或其他相关功能来使用您购买的服务。
                    </p>
                    <button
                      onClick={() => {
                        setShowActivationTutorial(false);
                        navigate('/ai-fleet');
                      }}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
                    >
                      <ExternalLink className="w-4 h-4" />
                      前往AI车队
                    </button>
                  </div>
                </div>
              </div>

              {/* 常见问题 */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-4">常见问题</h4>
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h5 className="font-medium text-gray-900 mb-2">Q: 激活失败怎么办？</h5>
                    <p className="text-gray-600 text-sm">
                      A: 请检查网络连接，稍后重试。如果问题持续存在，请联系客服支持。
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h5 className="font-medium text-gray-900 mb-2">Q: 卡密可以重复激活吗？</h5>
                    <p className="text-gray-600 text-sm">
                      A: 不可以。每张卡密只能激活一次，激活后无法撤销。
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h5 className="font-medium text-gray-900 mb-2">Q: 激活后服务什么时候生效？</h5>
                    <p className="text-gray-600 text-sm">
                      A: 大部分服务在激活后立即生效，少数服务可能需要等待1-5分钟。
                    </p>
                  </div>
                </div>
              </div>

              {/* 底部操作按钮 */}
              <div className="mt-8 pt-6 border-t border-gray-200 flex justify-between">
                <button
                  onClick={() => navigate('/support-sidebar')}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors text-sm"
                >
                  需要更多帮助？
                </button>
                <button
                  onClick={() => setShowActivationTutorial(false)}
                  className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
                >
                  我知道了
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast 通知 - 复制成功反馈 */}
      {showToast && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 transition-all duration-300 ease-out">
          <div className="bg-gray-900 text-white px-4 py-3 rounded-lg shadow-lg flex items-center space-x-2">
            <Check className="w-4 h-4 text-green-400" />
            <span className="text-sm font-medium">
              复制成功！
            </span>
          </div>
        </div>
      )}
    </div>
  );
} 